import { Close, OpenInNew } from '@mui/icons-material';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { _ } from '/@/locale';
import { getSessionLanguage } from '/@/store/session/selectors';
import { formatDate, toDegreesMinutesAndSeconds } from '/@/utils';
import { reverse } from '/@/utils/router';

type LocalizedLabel = { es: string; en: string; pt: string };

type PhenologyOption = { value: string; label: LocalizedLabel };

export type Marker = {
    markerType?: string;
    id?: string | number;
    name?: string;
    location?: string;
    latitude?: number;
    longitude?: number;
    lat?: number;
    lng?: number;
    manager?: string;
    contact?: string;
    plantingDate?: string;
    phenologicalStage?: string;
    type?: { name: LocalizedLabel };
};

type RenderMarkerDescriptionProps = {
    marker: Marker;
    onClose: () => void;
    phenologiesOptions: PhenologyOption[];
    isMobileView?: boolean;
};

const getGoogleMapsUrl = (marker: Marker) =>
    `https://www.google.com/maps/search/?api=1&query=${marker.latitude},${marker.longitude}`;

const DetailField = ({ label, children }: { label: string; children: ReactNode }) => (
    <Stack spacing={0}>
        <Typography variant='body2' color='text.secondary'>
            {label}
        </Typography>
        <Typography variant='body2' fontWeight='bold' color='primary' component='div'>
            {children}
        </Typography>
    </Stack>
);

const DescriptionHeader = ({ title, onClose }: { title: string; onClose: () => void }) => (
    <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        pl={2}
        pr={1}
        py={1}
        borderBottom={(t) => `1px solid ${t.palette.divider}`}
    >
        <Typography variant='subtitle1' color='primary'>
            {title}
        </Typography>
        <IconButton onClick={onClose} size='large' aria-label={_('Cerrar')}>
            <Close />
        </IconButton>
    </Stack>
);

const DescriptionShell = ({
    title,
    onClose,
    isMobileView,
    actions,
    children,
}: {
    title: string;
    onClose: () => void;
    isMobileView: boolean;
    actions: ReactNode;
    children: ReactNode;
}) => (
    <Stack height='100%'>
        <DescriptionHeader title={title} onClose={onClose} />
        <Stack spacing={2} p={2} overflow='auto' flex={1} height='100%'>
            {children}
        </Stack>
        {actions && (
            <Stack
                direction='row'
                justifyContent={isMobileView ? 'flex-end' : 'stretch'}
                px={2}
                py={2}
                borderTop={(t) => `1px solid ${t.palette.divider}`}
            >
                {actions}
            </Stack>
        )}
    </Stack>
);

const RenderMarkerDescription = ({
    marker,
    onClose,
    phenologiesOptions,
    isMobileView = false,
}: RenderMarkerDescriptionProps) => {
    const language = useSelector(getSessionLanguage) as keyof LocalizedLabel;
    const navigate = useNavigate();

    if (marker.markerType === 'field') {
        return (
            <DescriptionShell
                title={_('Lote')}
                onClose={onClose}
                isMobileView={isMobileView}
                actions={
                    <Button
                        variant={isMobileView ? 'contained' : 'outlined'}
                        fullWidth={!isMobileView}
                        onClick={() => navigate(reverse('fields.FieldDetail', { params: { fieldId: marker.id } }))}
                    >
                        {_('Ver más')}
                    </Button>
                }
            >
                <DetailField label={_('Nombre')}>
                    <Typography className='mp-mask' component='span' variant='inherit'>
                        {marker.name}
                    </Typography>
                </DetailField>
                <DetailField label={_('Ubicación')}>
                    <Typography className='mp-mask' component='span' variant='inherit'>
                        {marker.location}
                    </Typography>
                </DetailField>
            </DescriptionShell>
        );
    }

    const phenologicalStage = phenologiesOptions.find(({ value }) => value === marker.phenologicalStage);

    return (
        <DescriptionShell
            title={_('Ensayo')}
            onClose={onClose}
            isMobileView={isMobileView}
            actions={
                <Button
                    variant='contained'
                    color='primary'
                    fullWidth={!isMobileView}
                    endIcon={<OpenInNew />}
                    href={getGoogleMapsUrl(marker)}
                    target='_blank'
                    rel='noreferrer'
                >
                    {_('Abrir en Google Maps')}
                </Button>
            }
        >
            <DetailField label={_('Nombre')}>{marker.name}</DetailField>
            <DetailField label={_('Tipo')}>{marker.type?.name[language]}</DetailField>
            <DetailField label={_('Responsable')}>{marker.manager}</DetailField>
            <DetailField label={_('Contacto')}>{marker.contact}</DetailField>
            <DetailField label={_('Geoposición')}>
                <Stack spacing={0}>
                    <Typography variant='inherit' component='span'>
                        {_('Latitud')}: {toDegreesMinutesAndSeconds(marker.latitude)}
                    </Typography>
                    <Typography variant='inherit' component='span'>
                        {_('Longitud')}: {toDegreesMinutesAndSeconds(marker.longitude)}
                    </Typography>
                </Stack>
            </DetailField>
            <DetailField label={_('Fecha de siembra')}>
                {marker.plantingDate ? formatDate(new Date(marker.plantingDate)) : '-'}
            </DetailField>
            <DetailField label={_('Estadio fenológico estimado')}>{_(phenologicalStage?.label)}</DetailField>
            <Typography variant='caption' color='text.secondary'>
                {_(
                    '*Para lograr una cosecha de calidad, a partir de la madurez fisiológica el grano debe perder humedad. Esto dependerá de las condiciones climáticas de demanda atmosférica.'
                )}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
                {_('**La fenología de los ensayos se calcula tomando como referencia al híbrido DK72-10.')}
            </Typography>
        </DescriptionShell>
    );
};

export default RenderMarkerDescription;
